import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { CanvasRenderer, RenderedCanvas } from "@rosette/web_components/src/generic/render/RenderedCanvas.js";
import { useDiceController, useDiceDirective } from "@rosette/web_components/src/game/dice/DiceUIContext.js";
import type { Seconds } from "@rosette/ts/src/util/units.js";
import { getTimeSeconds, LONG_TIME_AGO } from "@rosette/ts/src/util/utils.js";
import { RoyalUrRoll } from "@rosette/ts/src/business/game/royalur/RoyalUrRoll.js";
import { RoyalUrDice } from "@rosette/ts/src/business/game/royalur/RoyalUrDice.js";
import { clamp, easeInSine, easeOutSine, floor } from "@rosette/ts/src/util/numbers.js";
import type { ImageAsset } from "@/components/assets/ImageAsset.js";
import type { DiceController } from "@rosette/ts/src/business/game/controller/dice/DiceController.js";
import type { DiceDirective } from "@rosette/ts/src/business/game/controller/dice/DiceDirective.js";
import { LeftArrowIcon } from "@rosette/web_components/src/icon/LeftArrowIcon.js";
import { RightArrowIcon } from "@rosette/web_components/src/icon/RightArrowIcon.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { Key, registerKeyListener } from "@rosette/web_components/src/generic/KeyListener.js";
import { AnimatePresence, motion } from "framer-motion";
import type { GameTheme } from "@/components/game/theme/GameTheme.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import { DiceType } from "@rosette/ts/src/royalur/model/dice/DiceType.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { GameLayoutType } from "@rosette/web_components/src/game/useGameLayout.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { DiceStyle } from "@rosette/ts/src/business/game/DiceStyle.js";
import { useOptionalGameController } from "@/components/game/GameUIContext.js";
import { rand } from "@rosette/ts/src/util/random.js";


class DiceRenderer extends CanvasRenderer {
    /**
     * The time between changing the value of the rolling dice.
     */
    public static ROLLING_DICE_CHANGE_INTERVAL: Seconds = 0.235;

    /**
     * The time taken to fade in the dice rolling sound.
     */
    public static ROLLING_DICE_FADE_DURATION: Seconds = 0.25;

    /**
     * The time spent with the dice rolling.
     */
    static readonly DEFAULT_DICE_ROLL_DURATION: Seconds = 0.6;

    /**
     * The time spent with the dice rolling.
     */
    static readonly FAST_DICE_ROLL_DURATION: Seconds = 0.15;

    /**
     * The time taken to select all dice.
     */
    public static DEFAULT_DICE_SELECT_DURATION_PER_DICE: Seconds = 0.75 / 4;

    /**
     * The time taken to select all dice.
     */
    public static FAST_DICE_SELECT_DURATION_PER_DICE: Seconds = 0.5 / 4;

    /**
     * The time it takes for the dice to hit the ground
     * after falling from being rolled.
     */
    public static DEFAULT_DICE_FALL_DURATION_PER_DICE: Seconds = 0.45 / 4;

    /**
     * The time it takes for the dice to hit the ground
     * after falling from being rolled.
     */
    public static FAST_DICE_FALL_DURATION_PER_DICE: Seconds = 0.1 / 4;

    /**
     * The delay before the dice should flash red for when
     * a zero is rolled.
     */
    public static ZERO_FLASH_DELAY: Seconds = 0.15;

    /**
     * The time that dice should flash red for when
     * a zero is rolled.
     */
    public static ZERO_FLASH_DURATION: Seconds = 1.5;

    /**
     * The time that dice should flash red for when
     * a zero is rolled.
     */
    public static ZERO_FLASH_ENDGAME_DURATION: Seconds = 0.7;

    /**
     * The colour that the zero will flash.
     */
    public static ZERO_FLASH_COLOUR: string = "var(--text-color-danger)";

    /**
     * The time it takes for the dice to settle on the
     * ground after falling.
     */
    public static DICE_SETTLE_DURATION: Seconds = 0.3;

    /**
     * The time that the dice maintain focus before
     * popping the active directive.
     */
    public static ROLL_FOCUS_DURATION: Seconds = 0.55;

    /**
     * The delay to use before playing audio elements of the dice.
     */
    public static AUDIO_DELAY: Seconds = -0.05;

    private readonly controller: DiceController;
    private readonly playerFilter: PlayerType | null;
    private readonly theme: GameTheme;
    private readonly diceStyle: DiceStyle;
    private readonly autoDiceRolls: boolean;
    private readonly diceType: DiceType;
    private readonly dice: RoyalUrDice;
    private readonly numDie: number;
    private rollTextElement: HTMLElement | null = null;
    private valueTextElement: HTMLElement | null = null;
    private lastDirective: DiceDirective | null = null;

    private rollStartTime: Seconds = LONG_TIME_AGO;
    private rollEndTime: Seconds = LONG_TIME_AGO;
    private selectTime: Seconds = LONG_TIME_AGO;
    private randomRoll: RoyalUrRoll;
    private lastRoll: RoyalUrRoll;
    private randomRollChangeTime: Seconds = LONG_TIME_AGO;
    private noMovesErrorPlayed: boolean = false;
    private diceClickPlayed: boolean = false;
    private lastAudioSelected: number = 0;

    constructor(
        diceType: DiceType,
        theme: GameTheme,
        diceStyle: DiceStyle,
        autoDiceRolls: boolean,
        controller: DiceController,
        playerFilter: PlayerType | null,
    ) {
        super();
        this.controller = controller;
        this.playerFilter = playerFilter;
        this.theme = theme;
        this.diceStyle = diceStyle;
        this.autoDiceRolls = autoDiceRolls;
        this.diceType = diceType;
        this.dice = new RoyalUrDice(diceType);
        this.numDie = diceType.getDieCount();
        this.randomRoll = RoyalUrRoll.cast(this.dice.roll());
        this.lastRoll = this.randomRoll;
    }

    override attachListeners(): () => void {
        return () => {};
    }

    setRollTextElement(rollTextElement: HTMLElement | null) {
        this.rollTextElement = rollTextElement;
    }

    setValueTextElement(valueTextElement: HTMLElement | null) {
        const previousValueTextElement = this.valueTextElement;
        this.valueTextElement = valueTextElement;

        if (valueTextElement) {
            delete valueTextElement.dataset.allSelected;
        }

        if (previousValueTextElement !== null && previousValueTextElement !== valueTextElement) {
            previousValueTextElement.style.color = "";
            delete previousValueTextElement.dataset.allSelected;
        }
    }

    static getDiceSelectDurationPerDice(diceStyle: DiceStyle): number {
        return (
            diceStyle === DiceStyle.FAST
                ? DiceRenderer.FAST_DICE_SELECT_DURATION_PER_DICE
                : DiceRenderer.DEFAULT_DICE_SELECT_DURATION_PER_DICE
        );
    }

    getSelectDurationPerDice(): number {
        return DiceRenderer.getDiceSelectDurationPerDice(this.diceStyle);
    }

    static getDiceFallDurationPerDice(diceStyle: DiceStyle): number {
        return (
            diceStyle === DiceStyle.FAST
                ? DiceRenderer.FAST_DICE_FALL_DURATION_PER_DICE
                : DiceRenderer.DEFAULT_DICE_FALL_DURATION_PER_DICE
        );
    }

    getFallDurationPerDice(): number {
        return DiceRenderer.getDiceFallDurationPerDice(this.diceStyle);
    }

    static getDiceRollDuration(diceStyle: DiceStyle): number {
        return (
            diceStyle === DiceStyle.FAST
                ? DiceRenderer.FAST_DICE_ROLL_DURATION
                : DiceRenderer.DEFAULT_DICE_ROLL_DURATION
        );
    }

    getRollDuration(): number {
        return DiceRenderer.getDiceRollDuration(this.diceStyle);
    }

    private onNewDirective(lastDirective: DiceDirective | null, directive: DiceDirective) {
        const time = getTimeSeconds();

        if (directive.rolling) {
            // Start the dice rolling.
            if (this.rollStartTime < 0) {
                this.rollStartTime = time;
                this.fadeInDiceRollSounds();
            }

            if (directive.roll) {
                const dieCount = this.diceType.getDieCount();
                this.selectTime = time + this.getFallDurationPerDice() * dieCount;
                this.lastAudioSelected = 0;
                this.lastRoll = directive.roll;
                this.noMovesErrorPlayed = false;
            } else if (!directive.rollForever) {
                this.rollEndTime = time + this.getRollDuration();
            }
        } else {
            this.rollStartTime = LONG_TIME_AGO;
        }

        // Little animation showing that the dice can be rolled.
        if (
            directive.canBeRolled
            && !this.autoDiceRolls
            && lastDirective !== null
            && lastDirective.activePlayer === directive.activePlayer
            && this.rollTextElement !== null
        ) {
            delete this.rollTextElement.dataset.pulse;
            setTimeout(() => {
                if (this.rollTextElement !== null) {
                    this.rollTextElement.dataset.pulse = "";
                }
            });
        }

        // Automatically roll the dice.
        if (directive.canBeRolled && this.autoDiceRolls) {
            this.controller.handleRoll();
        }
    }

    playErrorSound() {
        this.theme.getErrorSound().play();
    }

    playDiceSelectSound(diceIndex: number) {
        this.theme.getDiceSelectSound(diceIndex).play();
    }

    fadeInDiceRollSounds() {
        if (this.diceStyle === DiceStyle.FAST)
            return;

        for (const sound of this.theme.getDiceRollSounds()) {
            sound.fade(0, 1, 1000);
        }
    }

    playDiceRollSound() {
        if (this.diceStyle === DiceStyle.FAST)
            return;

        const sound = this.theme.getDiceRollSound();
        const soundID = sound.play();

        const timeSinceStartRolling = getTimeSeconds() - this.rollStartTime;
        if (timeSinceStartRolling < DiceRenderer.ROLLING_DICE_FADE_DURATION) {
            sound.fade(
                timeSinceStartRolling / DiceRenderer.ROLLING_DICE_FADE_DURATION,
                1.0,
                1000 * (DiceRenderer.ROLLING_DICE_FADE_DURATION - timeSinceStartRolling),
                soundID,
            );
        }
    }

    calculateSelected(directive: DiceDirective, selectTime: number, selectDieCount: number): number {
        let selected = selectDieCount;
        if (directive.rolling) {
            if (directive.roll) {
                // Determine how many dice should be selected.
                const dieCount = this.diceType.getDieCount();
                const fallDuration = this.getFallDurationPerDice() * dieCount;
                const selectDuration = this.getSelectDurationPerDice() * selectDieCount;
                const selectProgress = (
                    (selectTime + fallDuration) / selectDuration
                );
                selected = clamp(floor(selectDieCount * selectProgress), 0, selectDieCount);
            } else {
                selected = 0;
            }
        }
        return selected;
    }

    override render(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
    ): void {
        let directive = this.controller.getActiveDirective();

        if (this.lastDirective === null || (directive !== this.lastDirective
            && (this.playerFilter === null || this.playerFilter === directive.activePlayer))) {

            const lastDirective = this.lastDirective;
            this.lastDirective = directive;
            this.onNewDirective(lastDirective, directive);
        } else {
            directive = this.lastDirective;
        }

        const dieCount = this.diceType.getDieCount();
        const trueRoll = (directive.roll ? directive.roll : this.lastRoll);
        const trueDie = trueRoll.getDie();
        const trueDieValue = trueRoll.value();
        const randomDie = (
            this.diceStyle === DiceStyle.FAST && directive.roll
                ? trueDie
                : this.randomRoll.getDie());
        const zeroEq4 = (
            directive.roll !== undefined
            && this.diceType === DiceType.THREE_BINARY_0EQ4
            && trueDieValue === 4
        );
        const animDieCount = (zeroEq4 ? dieCount + 1 : dieCount);

        // We use timings to control the animation.
        const time = getTimeSeconds();
        const animTime = time - this.rollStartTime;
        const selectTime = time - this.selectTime;
        const rollingChangeTime = time - this.randomRollChangeTime;
        const audioRollingChangeTime = rollingChangeTime - DiceRenderer.AUDIO_DELAY;

        const selected = this.calculateSelected(
            directive, selectTime, animDieCount,
        );
        const allSelected = (selected >= animDieCount);
        const audioSelected = this.calculateSelected(
            directive, selectTime - DiceRenderer.AUDIO_DELAY, animDieCount,
        );

        let valueTextColour = "";
        if (directive.rolling) {
            if (directive.roll) {
                // Animate flashing red when the player has no moves.
                const focusDuration = (
                    directive.noMoves
                        ? DiceRenderer.ZERO_FLASH_DELAY
                        : DiceRenderer.ROLL_FOCUS_DURATION
                );
                const flashDuration = (
                    directive.noMoves ? (
                        directive.endGame
                            ? DiceRenderer.ZERO_FLASH_ENDGAME_DURATION
                            : DiceRenderer.ZERO_FLASH_DURATION
                    ) : 0
                );
                const flashTime = selectTime - focusDuration;
                if (!this.noMovesErrorPlayed
                    && flashTime - DiceRenderer.AUDIO_DELAY - 0.3 >= 0 && directive.noMoves) {
                    this.playErrorSound();
                    this.noMovesErrorPlayed = true;
                }
                if (flashTime >= 0 && directive.noMoves) {
                    valueTextColour = DiceRenderer.ZERO_FLASH_COLOUR;
                }

                // Play the dice select sound.
                if (this.lastAudioSelected !== audioSelected) {
                    this.lastAudioSelected = audioSelected;
                    const audioIndex = audioSelected - 1;
                    const audioOrder = (audioIndex < 2 ? audioIndex * 2 : 1 + 2 * (audioIndex - 2));

                    let previouslyUp = 0;
                    for (let index = 0; index < audioIndex; ++index) {
                        const order = (index < 2 ? index * 2 : 1 + 2 * (index - 2));
                        if (trueDie[order].isUp()) {
                            previouslyUp += 1;
                        }
                    }

                    if (audioSelected > 0 && (zeroEq4 || !!trueDie[audioOrder]?.isUp())) {
                        this.playDiceSelectSound(zeroEq4 ? audioSelected - 1 : previouslyUp);
                    }
                }

                // Done rolling!
                if (selectTime > focusDuration + flashDuration) {
                    this.controller.popActiveDirective(directive);
                }
            } else {
                // Done rolling!
                if (!directive.rollForever && time >= this.rollEndTime) {
                    this.controller.popActiveDirective(directive);
                }
            }

            // Update the value of the rolling dice.
            if (selected < this.numDie
                && rollingChangeTime > DiceRenderer.ROLLING_DICE_CHANGE_INTERVAL) {

                this.randomRoll = RoyalUrRoll.cast(this.dice.roll());
                this.randomRollChangeTime = time + rand(-0.05, 0.05);
                this.diceClickPlayed = false;
            }

            // Play the dice click sound while rolling the dice.
            if (audioSelected < this.numDie - 1
                && audioRollingChangeTime > DiceRenderer.ROLLING_DICE_CHANGE_INTERVAL
                && !this.diceClickPlayed) {
                this.diceClickPlayed = true;
                this.playDiceRollSound();
            }
        }

        // Update the colour of the dice value text.
        if (this.valueTextElement) {
            this.valueTextElement.style.color = valueTextColour;

            if (allSelected) {
                this.valueTextElement.dataset.allSelected = "";
            } else {
                delete this.valueTextElement.dataset.allSelected;
            }
        }

        // Do the drawing!
        this.clearCanvas(ctx, width, height);

        const overflowSpace = width / 25;
        const space = (width + 2 * overflowSpace) / 2.5;
        const dieWidth = space * 0.7;

        const fallDuration = this.getFallDurationPerDice() * dieCount;
        const selectDuration = this.getSelectDurationPerDice() * dieCount;
        const settleDuration = DiceRenderer.DICE_SETTLE_DURATION;

        const halfSettleDuration = 0.5 * settleDuration;
        const selectInterval = selectDuration / this.numDie;

        for (let index = 0; index < this.numDie; ++index) {
            // Determine where to draw the die.
            let order;
            if (index < 2) {
                order = 2 * index;
            } else {
                order = 1 + 2 * (index - 2);
            }

            const top = (index < 2 ? 0.5 : 0.9) * dieWidth;
            const left = width / 2 - 0.5 * (dieCount / 2 - 0.5 - order) * space;

            // Determine when to show the dice as selected.
            let timeToSelect;
            if (directive.roll) {
                timeToSelect = order * selectInterval - selectTime;
            } else {
                timeToSelect = 999.9;
            }

            // Determine how big the dice should be rendered.
            let sizeModifier = (directive.rolling ? 1 : 0);

            if (timeToSelect > 0 && timeToSelect < fallDuration) {
                const t = 1 - timeToSelect / fallDuration;
                sizeModifier = 1 - easeInSine(t);
            } else if (timeToSelect <= 0) {
                if (timeToSelect > -halfSettleDuration) {
                    const t = timeToSelect / (-halfSettleDuration);
                    sizeModifier = 0.2 * easeOutSine(t);
                } else if (timeToSelect > -settleDuration) {
                    const t = (timeToSelect + halfSettleDuration) / (-halfSettleDuration);
                    sizeModifier = 0.2 * (1 - easeInSine(t));
                } else {
                    sizeModifier = 0;
                }
            } else {
                const rollDuration = this.getRollDuration();
                const animSizeTime = Math.min(rollDuration, 0.5);
                if (animTime < animSizeTime) {
                    sizeModifier = easeOutSine(animTime / animSizeTime);
                }
            }

            // The size change is jarring when it is too quick.
            if (this.diceStyle === DiceStyle.FAST) {
                sizeModifier *= 0.25;
            }

            // Paint the dice!
            const drawWidth = (1 + 0.2 * sizeModifier) * dieWidth;
            const isSelected = (order < selected);
            const die = (
                isSelected || (this.diceStyle === DiceStyle.FAST && selected > 0)
                    ? trueDie[index]
                    : randomDie[index]
            );
            const drawImage = this.theme.getDiceImage(die);
            const highlighted = (
                (isSelected && directive.roll !== undefined && die.isUp())
                || (allSelected && zeroEq4)
            );

            this.paintDice(
                ctx,
                drawImage,
                drawWidth,
                left,
                top,
                highlighted,
            );
        }
    }

    paintDice(
        ctx: CanvasRenderingContext2D,
        image: ImageAsset,
        size: number,
        centreLeft: number,
        centreTop: number,
        useLightShadow: boolean,
    ) {
        const shadow = this.theme.getDiceShadow(useLightShadow);
        const shadowSize = 1.05 * size;
        shadow.draw(
            ctx,
            centreLeft - shadowSize / 2,
            centreTop - shadowSize / 2,
            shadowSize, shadowSize,
        );
        image.draw(
            ctx,
            centreLeft - size / 2,
            centreTop - size / 2,
            size, size,
        );
    }
}


function useDiceRenderer(
    diceType: DiceType,
    diceStyle: DiceStyle,
    autoDiceRolls: boolean,
    diceController: DiceController,
    playerFilter: PlayerType | null,
): DiceRenderer {
    const theme = useGameTheme();
    return useMemo(
        () => new DiceRenderer(
            diceType, theme, diceStyle, autoDiceRolls,
            diceController, playerFilter,
        ),
        [diceType, theme, diceStyle, autoDiceRolls, diceController, playerFilter],
    );
}


interface DiceUIProps {
    gameLayout: GameLayoutType | "none";
    diceType: DiceType;
    leftPlayer: PlayerType;
    playerFilter: PlayerType | null;
    ignorePreferences?: boolean;
}


export default function DiceUI({
    gameLayout, diceType, leftPlayer, playerFilter, ignorePreferences,
}: DiceUIProps) {

    const user = useOptionalUser();
    const { preferences } = useGamePreferences(user);
    const diceStyle = ignorePreferences ? DiceStyle.DRAMATIC : DiceStyle.getByID(preferences.diceStyle);
    const autoDiceRolls = !ignorePreferences && preferences.autoDiceRolls;

    const gameController = useOptionalGameController();
    const diceController = useDiceController();
    const renderer = useDiceRenderer(
        diceType, diceStyle, autoDiceRolls,
        diceController, playerFilter,
    );
    const trueDirective = useDiceDirective();

    useEffect(() => {
        if (!gameController)
            return;

        return gameController.registerDiceDirectiveSink();
    }, [gameController]);

    const passRendererTheRollTextElement = useCallback((span: HTMLSpanElement) => {
        renderer.setRollTextElement(span);
    }, [renderer]);

    const passRendererTheValueTextElement = useCallback((span: HTMLSpanElement) => {
        renderer.setValueTextElement(span);
    }, [renderer]);

    // When we are animating the  dice fading away, we don't
    // want to keep it updated with new directives.
    const lastDirectiveRef = useRef<DiceDirective>(trueDirective);
    let directive = trueDirective;
    if (
        directive !== lastDirectiveRef.current
        && directive.activePlayer !== null
        && (playerFilter === null || directive.activePlayer === playerFilter)
    ) {

        lastDirectiveRef.current = directive;
    } else {
        directive = lastDirectiveRef.current;
    }

    const diceCanvasRef = useRef<HTMLCanvasElement | null>(null);

    // If the user presses space or enter, roll the dice.
    useEffect(() => registerKeyListener((event: KeyboardEvent) => {
        if (!Key.ENTER.isKey(event.key) && !Key.Q.isKey(event.key) && !Key.SPACE.isKey(event.key))
            return;

        const directive = diceController.getActiveDirective();
        if (!directive.canBeRolled)
            return;

        event.stopPropagation();
        diceController.handleRoll();
    }), [diceController]);

    const diceValue = directive.getValue();

    const isLandscape = (gameLayout === "landscape");
    const isSquare = (gameLayout === "square");
    const isDramatic = (diceStyle === DiceStyle.DRAMATIC);

    let diceText, diceTextClass, diceTextRef;
    if (diceValue === undefined) {
        diceText = (autoDiceRolls ? "" : "Roll");
        diceTextClass = cn(
            "scale-110",
            !isLandscape && "data-pulse:animate-roll-pulse",
            isDramatic && directive.rolling && "opacity-0",
        );
        diceTextRef = passRendererTheRollTextElement;
    } else {
        diceText = diceValue;
        diceTextClass = cn(
            "scale-150 text-strong",
            isDramatic && "opacity-0 data-all-selected:opacity-100",
        );
        diceTextRef = passRendererTheValueTextElement;
    }

    const activePlayer = directive.activePlayer;
    const arrowClassName = "absolute top-1/2 w-[0.8em] h-[0.8em] fill-text transition-[opacity,translate,scale] duration-300 ease-[ease-in]";
    const visibleArrowClassName = "-translate-y-1/2 scale-x-130";
    const leftArrowClassName = (activePlayer === leftPlayer ? visibleArrowClassName : "opacity-0 translate-x-[1em] -translate-y-1/2 scale-90");
    const rightArrowClassName = (activePlayer !== leftPlayer ? visibleArrowClassName : "opacity-0 -translate-x-[1em] -translate-y-1/2 scale-90");

    return (
        <div
            role={directive.canBeRolled ? "button" : undefined}
            className={cn(
                "group grow-0 relative block text-center",
                directive.canBeRolled && "cursor-pointer",
                isLandscape && "max-w-[20vw]",
                isSquare && "max-w-[25vw]",
            )}
            onClick={directive.canBeRolled ? () => diceController.handleRoll() : undefined}>

            <div className={cn(
                "game-text font-bold text-4xl m-0 min-h-[1.6em] translate-y-[0.15em] select-none",
                "transition-[translate,scale,opacity] duration-150 ease-[ease-in-out]",
                directive.canBeRolled ? "text-strong" : "text-body",
                directive.canBeRolled && isLandscape && "group-hover:text-emphasis group-hover:scale-110",
                isSquare && "max-md:text-2xl",
            )}>
                <span
                    key={diceValue === undefined ? "roll" : "value"}
                    className={cn(
                        "inline-block mx-[1.2em] select-none transition-opacity duration-150 ease-[ease-in-out]",
                        diceTextClass,
                        diceText === "" && "opacity-0",
                    )}
                    ref={diceTextRef}>

                    {diceText}
                </span>

                {gameLayout === "portrait" && (
                    <>
                        <LeftArrowIcon className={cn(arrowClassName, "left-[1em]", leftArrowClassName)} />
                        <RightArrowIcon className={cn(arrowClassName, "right-[1em]", rightArrowClassName)} />
                    </>
                )}
            </div>

            <RenderedCanvas
                canvasRef={diceCanvasRef}
                className={cn(
                    "block w-[18em] h-[8.5em] max-w-full",
                    isSquare && "max-md:w-[9em] max-md:h-[4.25em]",
                )}
                renderer={renderer}
                updateEveryFrame={playerFilter === null || playerFilter === trueDirective.activePlayer} />

            <AnimatePresence>
                {trueDirective.noMoves
                    && trueDirective.activePlayer !== null
                    && (playerFilter === null || playerFilter === trueDirective.activePlayer)
                    && (
                    <motion.div
                        className={cn(
                            "absolute left-1/2 -translate-x-1/2 bg-danger py-[0.3em] px-[1em] rounded-[0.5em]",
                            "text-white whitespace-nowrap overflow-hidden shadow-md",
                            isLandscape ? "top-full text-2xl" : "bottom-[1em] text-3xl",
                        )}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                            opacity: 0,
                            transition: { duration: 0.2 },
                        }}
                        transition={{
                            delay: DiceRenderer.getDiceSelectDurationPerDice(diceStyle) * 4 - 0.1,
                            duration: 0.2,
                        }}>

                        No moves
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
